import { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, RefreshCw, Edit, Trash2, Search, Plane, Globe } from 'lucide-react';
import { ViewToggle } from '@/components/shared/ViewToggle';
import { AirportCard } from '@/components/airports/AirportCard';
import { useFetchAirports } from '@/hooks/useAirports';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCountries } from '@/hooks/useCountries';
import { useCities } from '@/hooks/useCities';
import { SmartButton } from '@/components/ui/interactive/SmartButton';
import { DynamicCard } from '@/components/ui/interactive/DynamicCard';
import { RealTimeUpdater } from '@/components/ui/interactive/RealTimeUpdater';
import { AdvancedSearch, SearchConfig } from '@/components/ui/search-advanced';
import { OptimizedTable, TableColumn } from '@/components/ui/optimized-table';
import { DataExport } from '@/components/ui/data-export';
import { LoadingSpinnerWithText } from '@/components/ui/loading-spinner';
import { useOptimizedData } from '@/hooks/useOptimizedData';
import type { Airport } from '@/types/airport';
type AirportFormData = Omit<Airport, 'id' | 'created_at' | 'updated_at' | 'cities' | 'countries'>;
export default function Airports() {
  const queryClient = useQueryClient();
  const {
    data: airports = [],
    isLoading,
    refetch
  } = useFetchAirports();
  const [view, setView] = useState<'table' | 'cards'>('table');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAirport, setEditingAirport] = useState<Airport | null>(null);
  const [formData, setFormData] = useState<AirportFormData>({
    name_ar: '',
    name_en: '',
    iata_code: '',
    country_id: null,
    city_id: null
  });
  const {
    data: countries = []
  } = useCountries();
  const {
    data: cities = []
  } = useCities(formData.country_id || undefined);

  // تكوين البحث المتقدم
  const searchConfig: SearchConfig = useMemo(() => ({
    placeholder: "البحث في المطارات...",
    filters: [{
      id: 'country',
      label: 'الدولة',
      type: 'select',
      options: countries.map(country => ({
        value: country.name_ar,
        label: country.name_ar
      })),
      icon: Globe
    }, {
      id: 'code',
      label: 'رمز المطار',
      type: 'text',
      icon: Plane
    }]
  }), [countries]);

  // استخدام hook البيانات المحسن
  const {
    data: displayAirports,
    allData: filteredAirports,
    searchQuery,
    setSearchQuery,
    filters,
    addFilter,
    removeFilter,
    clearFilters,
    sortConfig,
    changeSort,
    pagination,
    changePage,
    stats
  } = useOptimizedData({
    data: airports,
    searchFields: ['name_ar', 'name_en', 'iata_code'],
    initialPageSize: 20
  });

  // تكوين أعمدة الجدول
  const tableColumns: TableColumn<Airport>[] = useMemo(() => [{
    key: 'name_ar',
    title: 'الاسم بالعربية',
    dataIndex: 'name_ar',
    sortable: true,
    render: (_, record) => <div className="font-medium text-card-foreground">{record.name_ar}</div>
  }, {
    key: 'name_en',
    title: 'الاسم بالإنجليزية',
    dataIndex: 'name_en',
    sortable: true,
    render: (_, record) => record.name_en || '-'
  }, {
    key: 'iata_code',
    title: 'رمز IATA',
    dataIndex: 'iata_code',
    render: (_, record) => <span className="px-2 py-1 bg-primary/10 text-primary rounded-md font-mono text-sm">
          {record.iata_code}
        </span>
  }, {
    key: 'city',
    title: 'المدينة',
    render: (_, record) => record.cities?.name_ar || '-'
  }, {
    key: 'country',
    title: 'الدولة',
    render: (_, record) => record.countries?.name_ar || '-'
  }, {
    key: 'actions',
    title: 'الإجراءات',
    align: 'center',
    render: (_, record) => <div className="flex gap-2 justify-center">
          <Button variant="outline" size="sm" onClick={() => handleEdit(record)}>
            تعديل
          </Button>
          <Button variant="destructive" size="sm" onClick={() => handleDelete(record.id)}>
            حذف
          </Button>
        </div>
  }], []);

  // الإشعارات في الوقت الفعلي
  useEffect(() => {
    const channel = supabase.channel('airports_changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'airports'
    }, payload => {
      console.log('Airport change detected:', payload);
      refetch();
      switch (payload.eventType) {
        case 'INSERT':
          toast.success('تم إضافة مطار جديد');
          break;
        case 'UPDATE':
          toast.info('تم تحديث بيانات مطار');
          break;
        case 'DELETE':
          toast.warning('تم حذف مطار');
          break;
      }
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);
  const createMutation = useMutation({
    mutationFn: async (newAirport: AirportFormData) => {
      const {
        error
      } = await supabase.from('airports').insert(newAirport);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("تمت إضافة المطار بنجاح");
      queryClient.invalidateQueries({
        queryKey: ['airports']
      });
      resetForm();
    },
    onError: error => {
      toast.error(`خطأ في إضافة المطار: ${error.message}`);
    }
  });
  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data
    }: {
      id: string;
      data: Partial<AirportFormData>;
    }) => {
      const {
        error
      } = await supabase.from('airports').update(data).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("تم تحديث المطار بنجاح");
      queryClient.invalidateQueries({
        queryKey: ['airports']
      });
      resetForm();
    },
    onError: error => {
      toast.error(`خطأ في تحديث المطار: ${error.message}`);
    }
  });
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const {
        error
      } = await supabase.from('airports').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("تم حذف المطار بنجاح");
      queryClient.invalidateQueries({
        queryKey: ['airports']
      });
    },
    onError: error => {
      toast.error(`خطأ في حذف المطار: ${error.message}`);
    }
  });
  const resetForm = useCallback(() => {
    setEditingAirport(null);
    setFormData({
      name_ar: '',
      name_en: '',
      iata_code: '',
      country_id: null,
      city_id: null
    });
    setIsDialogOpen(false);
  }, []);
  const handleEdit = useCallback((airport: Airport) => {
    setEditingAirport(airport);
    setFormData({
      name_ar: airport.name_ar,
      name_en: airport.name_en || '',
      iata_code: airport.iata_code,
      country_id: airport.country_id,
      city_id: airport.city_id
    });
    setIsDialogOpen(true);
  }, []);
  const handleDelete = useCallback(async (id: string) => {
    deleteMutation.mutate(id);
  }, [deleteMutation]);
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (editingAirport) {
      updateMutation.mutate({
        id: editingAirport.id,
        data: formData
      });
    } else {
      createMutation.mutate(formData);
    }
  }, [editingAirport, formData, updateMutation, createMutation]);
  const handleSearch = useCallback((query: string, searchFilters: any[]) => {
    setSearchQuery(query);
    searchFilters.forEach(filter => {
      addFilter(filter.id, filter.value, filter.type);
    });
  }, [setSearchQuery, addFilter]);
  const searchFilters = useMemo(() => filters.map(filter => {
    const config = searchConfig.filters?.find(f => f.id === filter.id);
    return {
      ...filter,
      label: config?.label || filter.id,
      options: config?.options
    };
  }), [filters, searchConfig.filters]);
  const handleManualRefresh = useCallback(async () => {
    toast.info("جاري تحديث البيانات...");
    try {
      await refetch();
      toast.success("تم تحديث البيانات بنجاح");
    } catch (error) {
      toast.error("حدث خطأ أثناء تحديث البيانات");
    }
  }, [refetch]);
  if (isLoading) {
    return <div className="container mx-auto p-6">
        <LoadingSpinnerWithText text="جاري تحميل المطارات..." />
      </div>;
  }
  return <div className="container mx-auto p-6 space-y-6" dir="rtl">
      <RealTimeUpdater tableName="airports" onUpdate={() => refetch()} showStatus={true}>
        {/* الهيدر */}
        <div className="flex items-center gap-2 mb-6">
          <h1 className="text-2xl font-bold text-card-foreground">المطارات</h1>
          
          <div className="flex gap-2 items-center">
            <ViewToggle view={view} onViewChange={setView} />
            
            <DataExport data={filteredAirports} columns={[{
            key: 'name_ar',
            title: 'الاسم بالعربية',
            dataIndex: 'name_ar'
          }, {
            key: 'name_en',
            title: 'الاسم بالإنجليزية',
            dataIndex: 'name_en'
          }, {
            key: 'iata_code',
            title: 'رمز IATA',
            dataIndex: 'iata_code'
          }, {
            key: 'city',
            title: 'المدينة',
            dataIndex: 'cities.name_ar'
          }, {
            key: 'country',
            title: 'الدولة',
            dataIndex: 'countries.name_ar'
          }]} filename="airports" formats={['csv', 'json']} />
            
            <SmartButton onClick={handleManualRefresh} variant="outline" size="sm" icon={RefreshCw}>
              تحديث
            </SmartButton>
            
            <SmartButton onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }} icon={Plus} successMessage="تم إضافة المطار" showSuccessState>
              إضافة مطار
            </SmartButton>
            
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogContent dir="rtl">
                <DialogHeader>
                  <DialogTitle>{editingAirport ? 'تعديل مطار' : 'إضافة مطار جديد'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name_ar">الاسم (العربية)</Label>
                    <Input id="name_ar" value={formData.name_ar} onChange={e => setFormData({
                    ...formData,
                    name_ar: e.target.value
                  })} required />
                  </div>
                  <div>
                    <Label htmlFor="name_en">الاسم (الإنجليزية)</Label>
                    <Input id="name_en" value={formData.name_en || ''} onChange={e => setFormData({
                    ...formData,
                    name_en: e.target.value
                  })} />
                  </div>
                  <div>
                    <Label htmlFor="iata_code">رمز IATA</Label>
                    <Input id="iata_code" value={formData.iata_code} onChange={e => setFormData({
                    ...formData,
                    iata_code: e.target.value.toUpperCase()
                  })} required />
                  </div>
                  <div>
                    <Label htmlFor="country_id">الدولة</Label>
                    <Select value={formData.country_id || ''} onValueChange={value => setFormData({
                    ...formData,
                    country_id: value,
                    city_id: null
                  })}>
                      <SelectTrigger><SelectValue placeholder="اختر الدولة" /></SelectTrigger>
                      <SelectContent>
                        {countries.map(c => <SelectItem key={c.id} value={c.id}>{c.name_ar}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="city_id">المدينة</Label>
                    <Select value={formData.city_id || ''} onValueChange={value => setFormData({
                    ...formData,
                    city_id: value
                  })} disabled={!formData.country_id || !cities.length}>
                      <SelectTrigger><SelectValue placeholder="اختر المدينة" /></SelectTrigger>
                      <SelectContent>
                        {cities.map(c => <SelectItem key={c.id} value={c.id}>{c.name_ar}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={resetForm}>إلغاء</Button>
                    <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                      {editingAirport ? 'حفظ التغييرات' : 'إضافة'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* البحث المتقدم */}
        <AdvancedSearch config={searchConfig} onSearch={handleSearch} initialQuery={searchQuery} initialFilters={searchFilters} />

        {/* المحتوى */}
        {view === 'cards' ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {displayAirports.map(airport => <DynamicCard key={airport.id} title={airport.name_ar} description={airport.name_en} badge={{
          text: airport.iata_code,
          variant: "outline"
        }} hoverEffect="lift" pulseOnUpdate onEdit={() => handleEdit(airport)} onDelete={() => handleDelete(airport.id)}>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Plane className="h-4 w-4" />
                  <span>{airport.cities?.name_ar || 'غير محدد'}</span>
                  <span>•</span>
                  <span>{airport.countries?.name_ar || 'غير محدد'}</span>
                </div>
              </DynamicCard>)}
          </div> : <OptimizedTable columns={tableColumns} data={displayAirports} loading={isLoading} pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: stats.filtered,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100]
      }} onPaginationChange={changePage} sortConfig={sortConfig ? {
        key: sortConfig.key as string,
        direction: sortConfig.direction
      } : undefined} onSortChange={config => config ? changeSort(config.key as keyof Airport, config.direction) : changeSort('name_ar')} rowKey="id" emptyText="لا توجد مطارات" size="middle" />}
        
        {stats.filtered === 0 && !isLoading && <div className="text-center py-12 text-muted-foreground">
            <p>لم يتم العثور على أي مطارات.</p>
          </div>}
      </RealTimeUpdater>
    </div>;
}